import type { Contract } from '../../api/schema'
import { $api } from '../../api'
import { apiClient } from '../../api/client'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Badge } from '../ui/badge'
import { AddContactModal } from './add-contact-modal'
import type { GroupSubmit } from './add-contact-modal'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useContracts, useContractTypes } from '../../hooks/useQueries'
import { useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { AppIcon } from '../ui/icon'
import { useLocale } from '../../contexts/LocaleContext'
import { usePermissions, holdsPermission } from '../../contexts/PermissionsContext'
import { getLocalizedName } from '../../lib/localization'
import {
    countHiddenClientRoles,
    filterClientRoles,
    groupContactsByIdentity,
    uniformStakeholderTypeNames,
    type ContactItem,
    type ContactRoleLine,
    type GroupContactItem,
} from '../../lib/asset-contacts'
import { SECTION_ICONS } from '../../lib/section-icons'
import { mdiTrashCanOutline } from '@mdi/js'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog'

/**
 * Code of the generic lightweight contract type that carries building standing
 * roles (syndic, gestionnaire, concierge, conseil syndical, notaire…). Adding a
 * contact creates a RELATION so it shows on the Contacts tab but not on the
 * Contracts list. See spec 2026-06-20-carnet-entretien-contacts §8.
 */
const RELATION_CONTRACT_TYPE_CODE = 'RELATION'

/**
 * Resolve the UID of the RELATION lightweight contract type for the org, so the
 * add-contact flow can tag the created contract as a relation. Returns undefined
 * if the type is not provisioned (the mutation then falls back to a plain
 * contract, preserving prior behaviour).
 */
function useRelationContractTypeUid(org: string): string | undefined {
    const { data: contractTypes = [] } = useContractTypes(org)
    return useMemo(() => {
        const relation = contractTypes.find(
            (ct) => (ct as { code?: string }).code === RELATION_CONTRACT_TYPE_CODE
        )
        return relation?.uid
    }, [contractTypes])
}

/**
 * Multi-step creation of a groupement (colocation / indivision / couple): the
 * group is a party in its own right, so we create it as a `party_group` user,
 * link each member to it with a `member_of` relation carrying its quote-part,
 * then bind the GROUP — not its members — to a lightweight RELATION contract as
 * a single stakeholder. Returns a submit handler + a pending flag.
 * See spec 2026-07-13-01-refonte-contacts-parties.
 */
function useCreateGroup(org: string, assetUid: string, relationTypeUid: string | undefined, onDone: () => void) {
    const [isPending, setIsPending] = useState(false)
    const submit = useCallback(async (g: GroupSubmit) => {
        setIsPending(true)
        try {
            const { data: group } = await apiClient.POST('/api/v1/organizations/{orgCode}/users', {
                params: { path: { orgCode: org } },
                body: {
                    name: g.name,
                    type: 'party_group' as const,
                    properties: { kind: g.kind },
                },
            })
            if (!group?.uid) return

            for (const m of g.members) {
                await apiClient.POST('/api/v1/organizations/{orgCode}/users/{userUid}/relations', {
                    params: { path: { orgCode: org, userUid: m.userUid } },
                    body: {
                        destination_user_uid: group.uid,
                        type: 'member_of' as const,
                        ...(m.sharesPercent !== undefined ? { properties: { shares_percent: m.sharesPercent } } : {}),
                    },
                })
            }

            await apiClient.POST('/api/v1/organizations/{orgCode}/contracts', {
                params: { path: { orgCode: org } },
                body: {
                    name: g.stakeholderTypeName,
                    ...(relationTypeUid ? { contract_type_uid: relationTypeUid } : {}),
                    status: 'open' as const,
                    stakeholders: [{ user_uid: group.uid, stakeholder_type_uid: g.stakeholderTypeUid }],
                    assets: [{ asset_uid: assetUid }],
                },
            })
            onDone()
        } finally {
            setIsPending(false)
        }
    }, [org, assetUid, relationTypeUid, onDone])
    return { submit, isPending }
}

/**
 * Standalone "Add a contact" control. Renders just the popover-trigger
 * button (not the contacts list) so it can be embedded in a section
 * header that lives above the contacts list — keeping the + affordance
 * top-aligned with the section title (issue #975).
 *
 * Mounting is decoupled from <AssetContacts/>; on success we invalidate
 * the contracts query so the list re-fetches without prop drilling.
 */
export function AssetContactsAddControl({ org, assetUid }: { org: string; assetUid: string }) {
    const [isOpen, setIsOpen] = useState(false)
    const queryClient = useQueryClient()
    const relationTypeUid = useRelationContractTypeUid(org)

    const createContractMutation = $api.useMutation('post', '/api/v1/organizations/{orgCode}/contracts', {
        onSuccess: () => {
            // Invalidate the contracts query so any AssetContacts list refreshes.
            queryClient.invalidateQueries({ predicate: (q) => {
                const k = q.queryKey
                return Array.isArray(k) && k.some((part) => typeof part === 'string' && part.includes('contracts'))
            } })
            setIsOpen(false)
        }
    })

    const handleCreate = (userUid: string, stakeholderTypeUid: string, stakeholderTypeName: string) => {
        createContractMutation.mutate({
            params: { path: { orgCode: org } },
            body: {
                name: stakeholderTypeName,
                // Adding a contact creates a lightweight RELATION, not a fake
                // draft contract, so it shows on Contacts but not the Contracts list.
                ...(relationTypeUid ? { contract_type_uid: relationTypeUid } : {}),
                status: 'open' as const,
                stakeholders: [{
                    user_uid: userUid,
                    stakeholder_type_uid: stakeholderTypeUid
                }],
                assets: [{ asset_uid: assetUid }]
            }
        })
    }

    const onGroupDone = useCallback(() => {
        queryClient.invalidateQueries({ predicate: (q) => {
            const k = q.queryKey
            return Array.isArray(k) && k.some((part) => typeof part === 'string' && part.includes('contracts'))
        } })
        setIsOpen(false)
    }, [queryClient])
    const { submit: submitGroup, isPending: groupPending } = useCreateGroup(org, assetUid, relationTypeUid, onGroupDone)

    if (!relationTypeUid) return null
    return (
        <AddContactModal
            isOpen={isOpen}
            onOpenChange={setIsOpen}
            org={org}
            onSubmit={handleCreate}
            onSubmitGroup={submitGroup}
            isSubmitting={createContractMutation.isPending || groupPending}
        />
    )
}

interface AssetContactsProps {
    org: string
    assetUid: string
    wrapped?: boolean // If true, includes Card wrapper
    hideHeader?: boolean // If true, omit the inner "Contacts" header (used by right panel)
    hideAddButton?: boolean // If true, do not render the inline Add button
                            // (the host renders one in its own header).
    headerSlot?: React.ReactNode // External handle to render the Add control elsewhere
    title?: string // Card title (default « Contacts »)
    addLabel?: string // Label of the Add control
    addVariant?: 'icon' | 'button'
    testId?: string
}

export function AssetContacts({ org, assetUid, wrapped = true, hideHeader = false, hideAddButton = false, headerSlot, title, addLabel, addVariant = 'icon', testId }: AssetContactsProps) {
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
    const { permissions, membershipKnown } = usePermissions()
    const canReadContracts = !membershipKnown || holdsPermission(permissions, 'contracts.read')
    // Removing an intervenant deletes its RELATION link: a contracts write.
    const canWriteContracts = membershipKnown && holdsPermission(permissions, 'contracts.write')
    const { t } = useTranslation('users')
    const { locale } = useLocale()
    const relationTypeUid = useRelationContractTypeUid(org)

    const {
        data: contractsData,
        isLoading,
        error,
        refetch
    } = useContracts(org, {
        assetUid: assetUid,
        include: ['stakeholders', 'user', 'stakeholder_type', 'members', 'contract_type'],
        // Contacts are derived from the asset's contracts, so this panel is a
        // `contracts.read`. `contracts` is not scope-aware yet, so a member
        // whose only access comes through an asset-scoped grant holds it
        // nowhere — and firing the request anyway is one 403 (and one red
        // notification) on every asset page load. Optimism is for declutter,
        // never for a network call (spec 2026-09-06-04, decision 6).
        enabled: canReadContracts,
    })

    // Mutation to create a new contract
    const createContractMutation = $api.useMutation('post', '/api/v1/organizations/{orgCode}/contracts', {
        onSuccess: () => {
            refetch()
            setIsCreateDialogOpen(false)
        }
    })

    const handleCreateContact = (userUid: string, stakeholderTypeUid: string, stakeholderTypeName: string) => {
        createContractMutation.mutate({
            params: { path: { orgCode: org } },
            body: {
                name: stakeholderTypeName,
                // Lightweight RELATION: appears on Contacts, hidden from the Contracts list.
                ...(relationTypeUid ? { contract_type_uid: relationTypeUid } : {}),
                status: 'open' as const,
                stakeholders: [{
                    user_uid: userUid,
                    stakeholder_type_uid: stakeholderTypeUid
                }],
                assets: [{ asset_uid: assetUid }]
            }
        })
    }

    const onGroupDone = useCallback(() => {
        refetch()
        setIsCreateDialogOpen(false)
    }, [refetch])

    // An intervenant without contract is held by its RELATION link alone:
    // removing it deletes that link, never a real contract.
    const [relationToRemove, setRelationToRemove] = useState<{ contractUid: string; name?: string } | null>(null)
    const removeRelationMutation = $api.useMutation('delete', '/api/v1/organizations/{orgCode}/contracts/{contractUid}', {
        onSuccess: () => {
            refetch()
            setRelationToRemove(null)
        },
    })
    const { submit: submitGroup, isPending: groupPending } = useCreateGroup(org, assetUid, relationTypeUid, onGroupDone)

    // Shape stakeholders into render items, grouped by contact identity: a
    // groupement (a party_group user) renders as one card listing its members,
    // a lone contact/company as an individual card — and each contact appears
    // ONCE regardless of how many contracts it is a stakeholder of. Every
    // contract/role the contact holds becomes a role line on that one card.
    // The grouping + CLIENT-role filtering rules live in lib/asset-contacts.ts
    // so the graph explorer's Contacts node kind derives the identical list.
    const [showClientRoles, setShowClientRoles] = useState(false)

    const allItems: ContactItem[] = useMemo(
        () => groupContactsByIdentity((contractsData ?? []) as Contract[]),
        [contractsData],
    )

    const hiddenCount = useMemo(() => countHiddenClientRoles(allItems), [allItems])

    const items: ContactItem[] = useMemo(
        () => filterClientRoles(allItems, showClientRoles),
        [allItems, showClientRoles],
    )

    const formatShares = (q?: number | null) =>
        q === null || q === undefined ? undefined : `${Math.round(q * 10) / 10}%`

    // Indivision shares should sum to 100 — surface a soft warning otherwise.
    const sharesWarning = (g: GroupContactItem): boolean => {
        if (g.groupKind !== 'indivision') return false
        const parts = g.members.map((m) => m.sharesPercent).filter((q): q is number => q !== null && q !== undefined)
        if (parts.length !== g.members.length || parts.length === 0) return false
        const sum = parts.reduce((a, b) => a + b, 0)
        return Math.abs(sum - 100) > 0.1
    }

    // Role after the name, once: when every visible role line on a card
    // shares the same stakeholder type, resolve its localized label so the
    // card can render it as a name suffix ("BOUVIER Securite Incendie
    // (Fournisseur)") and drop the per-line role badge. Returns undefined for
    // an empty or mixed-type role list — the caller then keeps a per-line
    // role badge instead. See lib/asset-contacts.ts uniformStakeholderTypeNames.
    const uniformRoleLabel = (roles: ContactRoleLine[]): string | undefined => {
        const names = uniformStakeholderTypeNames(roles)
        if (!names) return undefined
        return getLocalizedName(names, locale) || undefined
    }

    // Each role line is now identified by the contract's own type (e.g.
    // "Maintenance porte") rather than the repeated stakeholder-role badge —
    // the role moved to the name suffix above when uniform. In the mixed-role
    // case there is no name suffix to carry that information, so a per-line
    // role badge (roleNames) is kept alongside the contract-type badge.
    const contractLink = (
        contractUid?: string,
        contractName?: string,
        contractCode?: string,
        contractTypeNames?: { [key: string]: string },
        roleNames?: { [key: string]: string },
        contractTypeCode?: string,
        stakeholderTypeNames?: { [key: string]: string },
        contactName?: string,
    ) => contractTypeCode === RELATION_CONTRACT_TYPE_CODE ? (
        // No contract behind this role (spec 2026-10-25-25): the role and
        // « Sans contrat », never a link to a RELATION « contract ».
        <div className="flex items-center text-xs text-muted-foreground" data-testid={`contact-without-contract-${contractUid}`}>
            {stakeholderTypeNames && (
                <Badge variant="secondary" className="mr-2">{getLocalizedName(stakeholderTypeNames, locale)}</Badge>
            )}
            <span className="italic">{t('contacts.without_contract')}</span>
            {contractUid && canWriteContracts && (
                <button
                    type="button"
                    className="ml-auto inline-flex h-6 w-6 items-center justify-center rounded-md hover:bg-muted hover:text-destructive"
                    aria-label={t('contacts.remove_relation')}
                    title={t('contacts.remove_relation')}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setRelationToRemove({ contractUid, name: contactName }) }}
                    data-testid={`remove-relation-${contractUid}`}
                >
                    <AppIcon path={mdiTrashCanOutline} size="xs" />
                </button>
            )}
        </div>
    ) : (
        <div className="flex items-center text-xs text-muted-foreground">
            {roleNames && (
                <Badge variant="secondary" className="mr-2" data-testid={`stakeholder-role-badge-${contractUid}`}>
                    {getLocalizedName(roleNames, locale)}
                </Badge>
            )}
            {contractTypeNames && (
                <Badge variant="outline" className="mr-2" data-testid={`stakeholder-contract-type-badge-${contractUid}`}>
                    {getLocalizedName(contractTypeNames, locale)}
                </Badge>
            )}
            <Link
                to="/$org/contracts/$contractUid"
                params={{ org, contractUid: contractUid || '' }}
                className="hover:underline text-foreground"
                onClick={(e) => e.stopPropagation()}
                data-testid={`stakeholder-contract-link-${contractUid}`}
            >
                <AppIcon path={SECTION_ICONS.contracts} size="xs" className="inline mr-1" />
                {contractName}
                {contractCode && ` (${contractCode})`}
            </Link>
        </div>
    )

    const contactsList = (
        <div className="space-y-4">
            {items.map((item) => {
                const roleLabel = uniformRoleLabel(item.roles)
                return item.kind === 'individual' ? (
                    <div
                        key={`i-${item.uid}`}
                        className="relative p-3 border border-border rounded-lg hover:bg-muted/50 transition-colors"
                        data-testid={`contact-${item.uid}`}
                    >
                        <Link
                            to="/$org/users/$userUid"
                            params={{ org, userUid: item.uid || '' }}
                            className="block mb-2"
                            data-testid={`contact-link-${item.uid}`}
                        >
                            <div className="flex justify-between items-start">
                                <div>
                                    <div className="flex items-center gap-2">
                                        <div className="font-medium text-foreground hover:underline">
                                            {item.name}
                                        </div>
                                        {roleLabel && (
                                            <span
                                                className="text-sm text-muted-foreground"
                                                data-testid={`contact-role-${item.uid}`}
                                            >
                                                ({roleLabel})
                                            </span>
                                        )}
                                        {item.code && (
                                            <Badge
                                                variant="outline"
                                                className="text-xs font-mono text-muted-foreground"
                                                data-testid="contact-code"
                                                title={t('detail.code')}
                                            >
                                                {item.code}
                                            </Badge>
                                        )}
                                    </div>
                                    {(item.email || item.phone) && (
                                        <div className="text-sm text-muted-foreground">
                                            {item.email && <span data-testid={`contact-email-${item.uid}`}>{item.email}</span>}
                                            {item.email && item.phone && ' · '}
                                            {item.phone && <span data-testid={`contact-phone-${item.uid}`}>{item.phone}</span>}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </Link>
                        <div className="space-y-1">
                            {item.roles.map((role) => (
                                <div key={`${item.uid}-${role.contractUid}`}>
                                    {contractLink(
                                        role.contractUid,
                                        role.contractName,
                                        role.contractCode,
                                        role.contractTypeNames,
                                        roleLabel ? undefined : role.stakeholderTypeNames,
                                        role.contractTypeCode,
                                        role.stakeholderTypeNames,
                                        item.name,
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                ) : (
                    <div
                        key={`g-${item.groupUid}`}
                        className="relative p-3 border border-border rounded-lg hover:bg-muted/50 transition-colors"
                        data-testid={`contact-group-${item.groupUid}`}
                    >
                        <div className="mb-2 flex items-center gap-2">
                            <Link
                                to="/$org/users/$userUid"
                                params={{ org, userUid: item.groupUid }}
                                className="font-medium text-foreground hover:underline"
                                data-testid={`group-link-${item.groupUid}`}
                            >
                                {item.groupName}
                            </Link>
                            {roleLabel && (
                                <span
                                    className="text-sm text-muted-foreground"
                                    data-testid={`contact-role-${item.groupUid}`}
                                >
                                    ({roleLabel})
                                </span>
                            )}
                            <Badge variant="outline" className="text-xs" data-testid={`group-kind-${item.groupUid}`}>
                                {t(`contacts.group.kind.${item.groupKind}`, item.groupKind)}
                            </Badge>
                        </div>
                        <div className="space-y-1 mb-2">
                            {item.members.map((m) => (
                                <div key={m.uid} className="flex items-center gap-2">
                                    <Link
                                        to="/$org/users/$userUid"
                                        params={{ org, userUid: m.uid || '' }}
                                        className="text-sm text-foreground hover:underline"
                                        data-testid={`group-member-${m.uid}`}
                                    >
                                        {m.name}
                                    </Link>
                                    {formatShares(m.sharesPercent) && (
                                        <span className="text-xs text-muted-foreground" data-testid={`group-member-quote-${m.uid}`}>
                                            {formatShares(m.sharesPercent)}
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                        {sharesWarning(item) && (
                            <p className="text-xs text-amber-600 dark:text-amber-400 mb-2" data-testid={`group-quote-warning-${item.groupUid}`}>
                                {t('contacts.group.quote_part_warning')}
                            </p>
                        )}
                        <div className="space-y-1">
                            {item.roles.map((role) => (
                                <div key={`${item.groupUid}-${role.contractUid}`}>
                                    {contractLink(
                                        role.contractUid,
                                        role.contractName,
                                        role.contractCode,
                                        role.contractTypeNames,
                                        roleLabel ? undefined : role.stakeholderTypeNames,
                                        role.contractTypeCode,
                                        role.stakeholderTypeNames,
                                        item.groupName,
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )
            })}
            {hiddenCount > 0 && (
                <button
                    type="button"
                    onClick={() => setShowClientRoles((v) => !v)}
                    className="text-xs text-muted-foreground hover:text-foreground hover:underline"
                    data-testid="toggle-client-roles"
                >
                    {showClientRoles
                        ? t('contacts.hide_client_roles')
                        : t('contacts.show_client_roles', { count: hiddenCount })}
                </button>
            )}
        </div>
    )

    const displayContent = contactsList

    // Create dialog component (reused in both wrapped and unwrapped modes)
    const createContactDialog = (
        <AddContactModal
            isOpen={isCreateDialogOpen}
            onOpenChange={setIsCreateDialogOpen}
            org={org}
            onSubmit={handleCreateContact}
            onSubmitGroup={submitGroup}
            isSubmitting={createContractMutation.isPending || groupPending}
            triggerLabel={addLabel}
            triggerVariant={addVariant}
        />
    )

    // Offered only once the RELATION type is known: without it the link would
    // be created as a typeless contract and counted among the real ones.
    const addContactBtn = !isLoading && !error && !hideAddButton && relationTypeUid ? createContactDialog : null

    const removeRelationDialog = (
        <AlertDialog open={relationToRemove !== null} onOpenChange={(o) => { if (!o) setRelationToRemove(null) }}>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>{t('contacts.remove_relation_title')}</AlertDialogTitle>
                    <AlertDialogDescription>{t('contacts.remove_relation_description', { name: relationToRemove?.name ?? '' })}</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel data-testid="remove-relation-cancel">{t('common:actions.cancel', 'Cancel')}</AlertDialogCancel>
                    <AlertDialogAction
                        disabled={removeRelationMutation.isPending}
                        onClick={() => relationToRemove && removeRelationMutation.mutate({ params: { path: { orgCode: org, contractUid: relationToRemove.contractUid } } })}
                        data-testid="remove-relation-confirm"
                    >
                        {t('contacts.remove_relation')}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    )

    if (wrapped) {
        return (
            <Card data-testid={testId}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                    <CardTitle className="text-lg">{title ?? t('contacts.title', 'Contacts')}</CardTitle>
                    {addContactBtn}
                </CardHeader>
                <CardContent>
                    {items.length === 0 && !isLoading
                        ? <p className="text-sm text-muted-foreground" data-testid={testId ? `${testId}-empty` : undefined}>{t('contacts.empty_asset')}</p>
                        : displayContent}
                </CardContent>
                {removeRelationDialog}
            </Card>
        )
    }

    return (
        <div data-testid={testId}>
            {removeRelationDialog}
            {!hideHeader ? (
                <div className="flex flex-row items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-foreground">{title ?? t('contacts.title', 'Contacts')}</h3>
                    {addContactBtn}
                </div>
            ) : (
                addContactBtn && <div className="flex justify-end mb-2">{addContactBtn}</div>
            )}
            {headerSlot}
            {displayContent}
        </div>
    )
}